/* eslint-disable jira/filename-case */
import React, { type ComponentType } from 'react';
import {
	AnalyticsSource,
	type ContextResult,
} from '@jira/platform__analytics-web-react/src/components/decorators.tsx';
import ErrorBoundary from '@jira/platform__error-boundary/src/ErrorBoundary.tsx';
import { SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { Props as LegacyApiBridgeProps } from './legacy-api-bridge/types.tsx';
import type { LegacyBreadcrumbConnectorProps } from './legacy-breadcrumb-connector/types.tsx';
import type { Props as LegacyBreadcrumbRendererProps } from './legacy-breadcrumb-renderer/index.tsx';
import type { LegacyBreadcrumbStateProviderProps } from './legacy-breadcrumb-state/types.tsx';
import type { LegacyBreadcrumbAdapterProps } from './types.tsx';

type InjectedProps = {
	LegacyBreadcrumbRenderer: ComponentType<LegacyBreadcrumbRendererProps>;
	LegacyBreadcrumbState: ComponentType<LegacyBreadcrumbStateProviderProps>;
	LegacyApiBridge: ComponentType<LegacyApiBridgeProps>;
	LegacyBreadcrumbConnector: ComponentType<LegacyBreadcrumbConnectorProps>;
};

type Props = LegacyBreadcrumbAdapterProps & InjectedProps;

const LegacyBreadcrumbAdapter = ({
	LegacyBreadcrumbRenderer,
	LegacyBreadcrumbState,
	LegacyApiBridge,
	LegacyBreadcrumbConnector,
}: Props): React.JSX.Element => (
	<LegacyBreadcrumbState>
		{({ api, state }) => (
			<>
				<LegacyApiBridge
					onInsertItem={api.insertItem}
					onRemoveItem={api.removeItem}
					onSetRenderTarget={api.setTargetElement}
				/>
				{
					// Only activate the breadcrumb connector if there is a breadcrumb render target
					state.targetElement && (
						<ErrorBoundary id="legacy-breadcrumb-adapter">
							<LegacyBreadcrumbConnector legacyTrail={state.trail} />
							<LegacyBreadcrumbRenderer targetElement={state.targetElement} />
						</ErrorBoundary>
					)
				}
			</>
		)}
	</LegacyBreadcrumbState>
);

const ExportedComponent: ContextResult<
	({
		LegacyBreadcrumbRenderer,
		LegacyBreadcrumbState,
		LegacyApiBridge,
		LegacyBreadcrumbConnector,
	}: Props) => React.JSX.Element,
	InjectedProps
> = AnalyticsSource('legacyBreadcrumbs', SCREEN)(LegacyBreadcrumbAdapter);
export default ExportedComponent;
